import { EVENT_NAMES } from 'services/analytics/eventNames';
import { getLastEvent } from 'services/analytics/getLastEvent';
import { trackEvent } from 'services/analytics/trackEvent';
import {
  LoginSuccessStateTypes,
  SecondaryNavigationClickLocationTypes,
  MainNavigationClickActionTypes,
  SecondaryNavigationClickActionTypes,
  ChangePasswordClickActionTypes,
  InviteNewUserActionTypes,
  ExportProjectListGroupTypeTypes,
  ExportProjectListSuccessStateTypes,
  MainNavigationClickLocationTypes,
  ProjectGroupTypeTypes,
  LOGIN_CLICK_TEXT,
  ViewProjectDetailsClickLocationTypes,
  ViewStageDetailsClickLocationTypes,
  MapClickClickActionTypes,
  ScountAgentChatViewSections,
  DocumentViewSections,
  ProjectNotesClickActionTypes,
} from 'utils/analytics/constants';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import type { User } from 'state/auth';

export interface GAEventData {
  [key: string]: any;
}

export type TrackPageViewDataType = {
  sectionName: string;
  subSectionName?: string;
};

export const trackPageView = (data: TrackPageViewDataType) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.PAGE_VIEW, {
    section_name: data.sectionName,
    sub_section_name: data.subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};

export const trackSaveEvent = (data: GAEventData) => {
  trackEvent(EVENT_NAMES.SAVE_PROJECT, {
    saveProjectNewState: data.saveProjectNewState,
    saveProjectPastState: data.saveProjectPastState,
    projectId: data.projectId,
  });
};

export const trackFilterEvent = (filterEvent: string) => {
  trackEvent(EVENT_NAMES.FILTER_PROJECTS, {
    filtersUsed: [
      {
        filterType: 'save_project_state',
        filterValue: filterEvent,
      },
    ],
  });
};

export const trackProjectMaterials = (filePath: string) => {
  let projectId;

  const params = new URLSearchParams(window.location.search);

  const leadParam = params.get(QUERY_PARAM.lead);

  if (leadParam) {
    // Use 'lead' parameter if it's not empty
    projectId = leadParam;
  } else {
    // Fallback to existing slug logic
    const pathSegments = window.location.pathname.split('/projects/');
    projectId = pathSegments[1] || '';
  }

  trackEvent(EVENT_NAMES.PROJECT_MATERIALS, {
    projectId,
    filePath,
  });
};

type TTrackAuthentication = {
  userId: User['id'];
  companyId: User['company_id'];
};

export const trackAuthentication = ({ userId, companyId }: TTrackAuthentication) => {
  trackEvent(EVENT_NAMES.MER_AUTHENTICATION, {
    user_id: userId,
    company_id: companyId,
  });
};

export const trackLogin = ({
  userId,
  companyId,
  clickText = LOGIN_CLICK_TEXT,
  successState,
}: {
  userId?: User['id'];
  companyId?: User['company_id'];
  clickText?: string;
  successState: LoginSuccessStateTypes;
}) => {
  trackEvent(EVENT_NAMES.LOGIN, {
    user_id: userId,
    company_id: companyId,
    click_text: clickText,
    success_state: successState,
  });
};

export const trackChangePassword = ({
  clickAction,
  clickText,
}: {
  clickAction: ChangePasswordClickActionTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.CHANGE_PASSWORD, {
    click_action: clickAction,
    click_text: clickText,
  });
};

export const trackInviteNewUser = ({
  clickAction,
  clickText,
}: {
  clickAction: InviteNewUserActionTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.NEW_USER, {
    click_action: clickAction,
    click_text: clickText,
  });
};

export const trackMainNavigation = ({
  clickAction,
  clickLocation,
  clickText,
}: {
  clickAction: MainNavigationClickActionTypes;
  clickLocation: MainNavigationClickLocationTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.MAIN_NAVIGATION, {
    click_action: clickAction,
    click_location: clickLocation,
    click_text: clickText,
  });
};

export const trackSecondaryNavigation = ({
  clickAction,
  clickLocation,
  clickText,
}: {
  clickAction: SecondaryNavigationClickActionTypes;
  clickLocation: SecondaryNavigationClickLocationTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.SECONDARY_NAVIGATION, {
    click_action: clickAction,
    click_location: clickLocation,
    click_text: clickText,
  });
};

export const trackExportProjectList = ({
  groupId,
  groupType,
  linkPath,
  clickText,
  successState,
}: {
  groupId: string | number;
  groupType: ExportProjectListGroupTypeTypes;
  linkPath?: string;
  clickText?: string;
  successState: ExportProjectListSuccessStateTypes;
}) => {
  trackEvent(EVENT_NAMES.EXPORT_PROJECT_LIST, {
    group_id: groupId,
    group_type: groupType,
    link_path: linkPath,
    click_text: clickText,
    success_state: successState,
  });
};

export const trackErrorToast = ({ errorMessage }: { errorMessage?: string }) => {
  trackEvent(EVENT_NAMES.ERROR, { error_message: errorMessage });
};

export const trackProjectListClick = ({
  projectId,
  documentId,
  groupId,
  groupType,
  clickText,
}: {
  projectId?: string;
  documentId?: string;
  groupId?: string;
  groupType?: string;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.PROJECT_LIST_CLICK, {
    project_id: projectId,
    document_id: documentId,
    group_id: groupId,
    group_type: groupType,
    click_text: clickText,
  });
};

export const trackViewProjectDetails = ({
  projectId,
  groupId,
  groupType,
  clickLocation,
  clickText,
}: {
  projectId: string;
  groupId?: string;
  groupType?: ProjectGroupTypeTypes;
  clickLocation?: ViewProjectDetailsClickLocationTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.VIEW_PROJECT_DETAILS, {
    project_id: projectId,
    group_id: groupId,
    group_type: groupType,
    click_location: clickLocation,
    click_text: clickText,
  });
};

export const trackViewStageDetails = ({
  projectId,
  groupId,
  groupType,
  stageId,
  stageType,
  clickLocation,
  clickText,
}: {
  projectId?: string;
  groupId?: string;
  groupType?: ProjectGroupTypeTypes;
  stageId?: string;
  stageType?: string;
  clickLocation?: ViewStageDetailsClickLocationTypes;
  clickText?: string;
}) => {
  trackEvent(EVENT_NAMES.VIEW_STAGE_DETAILS, {
    project_id: projectId,
    group_id: groupId,
    group_type: groupType,
    stage_id: stageId,
    stage_type: stageType,
    click_location: clickLocation,
    click_text: clickText,
  });
};

export const trackMapClick = ({
  groupId,
  groupType,
  clickAction,
}: {
  groupId: string;
  groupType: ProjectGroupTypeTypes;
  clickAction: MapClickClickActionTypes;
}) => {
  trackEvent(EVENT_NAMES.MAP_CLICK, {
    group_id: groupId,
    group_type: groupType,
    click_action: clickAction,
  });
};

export const trackAIResearchAssistantInteraction = ({
  projectId,
  documentId,
  sectionName,
  subSectionName,
  clickText,
}: {
  projectId: string;
  documentId: string;
  sectionName: string;
  subSectionName?: string;
  clickText: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.AI_RESEARCH_ASSISTANT, {
    project_id: projectId,
    document_id: documentId,
    click_text: clickText,
    section_name: sectionName,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};

export const trackSearch = ({
  search_input,
  filter_name,
}: {
  search_input: string;
  filter_name?: string;
}) => {
  trackEvent(EVENT_NAMES.SEARCH, {
    search_input,
    filter_name,
  });
};

export const trackSaveCompany = ({
  groupIds,
  groupType,
  companyId,
  sectionName,
  subSectionName,
  clickText,
}: {
  groupIds: number[];
  groupType: ExportProjectListGroupTypeTypes;
  companyId: number;
  sectionName: string;
  subSectionName?: string;
  clickText: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.SAVE_COMPANY, {
    group_ids: groupIds,
    group_type: groupType,
    company_id: companyId,
    click_text: clickText,
    click_action: 'save',
    section_name: sectionName,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};

export const trackDownloadProject = ({
  projectId,
  clickText,
  sectionName,
  subSectionName,
}: {
  projectId: string;
  clickText: string;
  sectionName: string;
  subSectionName?: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.DOWNLOAD_PROJECT, {
    project_id: projectId,
    section_name: sectionName,
    click_text: clickText,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};

export const trackShareProject = ({
  projectId,
  clickText,
  sectionName,
  subSectionName,
}: {
  projectId: string;
  clickText: string;
  sectionName: string;
  subSectionName?: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.SHARE_PROJECT, {
    project_id: projectId,
    section_name: sectionName,
    click_text: clickText,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};

export const trackScoutAgentInteraction = ({
  subSectionName,
  clickText,
  clickAction,
  scoutAgentChatId,
}: {
  subSectionName?: string;
  clickText: string;
  clickAction: 'create_new_chat' | 'send_message' | 'select_previous_chat';
  scoutAgentChatId?: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.SCOUT_AGENT, {
    click_text: clickText,
    section_name: ScountAgentChatViewSections.SCOUT_AGENT_PAGE,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
    click_action: clickAction,
    scout_agent_chat_id: scoutAgentChatId,
  });
};

export const trackEnrichContactsRequest = ({
  subSectionName,
  contactId,
  documentId,
}: {
  subSectionName?: string;
  contactId: string;
  documentId: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.ENRICH_CONTACT, {
    section_name: DocumentViewSections.DOCUMENT_MODAL,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
    contact_id: contactId,
    document_id: documentId,
  });
};

export const trackProjectNote = ({
  projectId,
  clickAction,
  clickText,
  sectionName,
  subSectionName,
}: {
  projectId: string | number;
  clickAction: ProjectNotesClickActionTypes;
  clickText: string;
  sectionName: string;
  subSectionName?: string;
}) => {
  const lastEvent = getLastEvent(EVENT_NAMES.PAGE_VIEW);
  trackEvent(EVENT_NAMES.PROJECT_NOTE, {
    project_id: `${projectId}`,
    click_action: clickAction,
    click_text: clickText,
    section_name: sectionName,
    sub_section_name: subSectionName,
    previous_section_name: lastEvent?.section_name,
    previous_sub_section_name: lastEvent?.sub_section_name,
  });
};
